import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { Reveal, SectionHead } from "@/components/motion";
import { TIERS, quote } from "@/lib/pricing";

const Row = ({ label, value, strong, testid }) => (
  <div className="flex items-baseline justify-between gap-4 border-t border-current/10 py-3 text-sm">
    <span className="opacity-75">{label}</span>
    <span className={strong ? "font-semibold" : ""} data-testid={testid}>{value}</span>
  </div>
);

const TierCard = ({ tier }) => {
  const { t, eur } = useI18n();
  const p = t.pricing;
  const q = quote(tier.qty);
  const hero = tier.id === "reseau";
  return (
    <div data-testid={`tier-${tier.id}`} className={`relative flex flex-col p-7 transition-transform duration-500 hover:-translate-y-1 ${hero ? "bg-[var(--navy)] text-white ring-2 ring-[var(--gold)] lg:-my-8 lg:py-12" : "border border-[#0B1530]/15 bg-white text-[var(--navy)]"}`}>
      {hero && (
        <span className="absolute -top-3.5 left-7 bg-[var(--gold)] px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--night)]" data-testid="tier-reseau-badge">{p.badge}</span>
      )}
      <p className="font-display text-3xl font-extrabold uppercase">{p.tiers[tier.id].name}</p>
      <p className={`mt-1 text-sm ${hero ? "text-[var(--steel)]" : "text-[#3B4A66]"}`}>{p.tiers[tier.id].desc}</p>
      <p className={`mt-6 font-mono text-[11px] uppercase tracking-[0.2em] ${hero ? "text-[var(--gold)]" : "text-[#8A6A2C]"}`}>{p.qty} : {tier.qty} MB300</p>
      <p className="font-display mt-1 text-6xl font-black leading-none" data-testid={`tier-${tier.id}-total`}>{eur(q.total)}</p>
      <p className={`text-sm ${hero ? "text-[var(--steel)]" : "text-[#3B4A66]"}`}>{t.common.ht} — {p.total}</p>
      <div className="mt-6">
        <Row label={p.unitPrice} value={`${eur(q.unit)} ${t.common.ht}`} strong testid={`tier-${tier.id}-unit`} />
        <Row label={p.deposit} value={eur(q.deposit)} testid={`tier-${tier.id}-deposit`} />
        <Row label={p.balance} value={eur(q.balance)} testid={`tier-${tier.id}-balance`} />
        <Row label={q.saving ? `${p.saving} ${p.savingVs}` : p.saving} value={q.saving ? eur(q.saving) : p.noSaving} strong={!!q.saving} testid={`tier-${tier.id}-saving`} />
      </div>
      <Link to={`/commander?qty=${tier.qty}`} data-testid={`tier-${tier.id}-order`} className={`group mt-auto flex items-center justify-between px-5 py-4 text-xs font-bold uppercase tracking-[0.16em] transition-colors ${hero ? "mt-8 bg-[var(--gold)] text-[var(--night)] hover:bg-[var(--gold-hi)]" : "mt-8 border border-[var(--navy)] hover:bg-[var(--navy)] hover:text-white"}`}>
        {p.choose}<ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
  );
};

export const Pricing = () => {
  const { t, eur } = useI18n();
  const p = t.pricing;
  return (
    <section id="tarifs" className="relative bg-[var(--inox)] py-24 text-[var(--navy)] lg:py-32" data-testid="pricing-section">
      <div className="container-x">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <SectionHead index="04" eyebrow={p.eyebrow} title={p.title} light className="lg:col-span-6" />
          <Reveal delay={0.1} className="lg:col-span-6 lg:text-right" data-testid="pricing-main">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A6A2C]">{p.from}</p>
            <p className="font-display text-7xl font-black leading-none sm:text-8xl">{eur(9900)}<span className="mt-2 block text-2xl font-bold sm:ml-2 sm:mt-0 sm:inline">{p.perUnit}</span></p>
            <p className="mt-3 text-sm text-[#3B4A66] lg:ml-auto lg:max-w-md" data-testid="pricing-main-note">{p.mainNote}</p>
          </Reveal>
        </div>
        <div className="mt-20 grid gap-6 lg:grid-cols-3 lg:items-stretch">
          {TIERS.map((tier, i) => (
            <Reveal key={tier.id} delay={i * 0.08} className="flex [&>div]:w-full"><TierCard tier={tier} /></Reveal>
          ))}
        </div>
        <Reveal className="mt-16 grid gap-6 bg-[var(--night)] p-7 text-white md:grid-cols-12 md:items-center" data-testid="pricing-network">
          <div className="md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">{p.more.title}</p>
            <p className="font-display mt-1 text-4xl font-extrabold uppercase">{p.more.tag}</p>
            <p className="text-[var(--steel)]">{p.more.text}</p>
          </div>
          <Link to="/commander?qty=4%2B" data-testid="pricing-network-cta" className="md:col-span-4 border border-[var(--gold)] px-5 py-4 text-center text-xs font-bold uppercase tracking-[0.16em] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--night)] transition-colors">{p.more.cta}</Link>
        </Reveal>
        <div className="mt-8 grid gap-3 text-sm text-[#3B4A66] md:grid-cols-2">
          <p className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#8A6A2C]" />{p.included}</p>
          <p data-testid="volume-condition">{p.volumeCond}</p>
        </div>
      </div>
    </section>
  );
};
